import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ContentCopyRounded,
  CheckRounded,
  PeopleAltRounded,
  PrintRounded,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";
import {
  buildCheckInUrl,
  subscribeRecentVisits,
  subscribeDepartments,
  sendClinicBroadcast,
} from "services/clinicService";

const VISIT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const toDate = (checkedInAt) =>
  checkedInAt?.toDate ? checkedInAt.toDate() : null;

const ClinicMessagingPage = ({ data }) => {
  const clinicId = data?.id;
  const checkInUrl = clinicId ? buildCheckInUrl(clinicId) : "";

  const [copied, setCopied] = useState(false);
  const [visits, setVisits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const qrRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sentSummary, setSentSummary] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: { body: "", departmentId: "" },
    mode: "onChange",
  });

  const selectedDepartmentId = watch("departmentId");

  useEffect(() => {
    if (!clinicId) return undefined;
    const unsubscribe = subscribeRecentVisits(
      clinicId,
      (rows) => setVisits(rows),
      (err) => console.error("[ClinicMessagingPage] visits subscription failed:", err),
    );
    return () => unsubscribe();
  }, [clinicId]);

  useEffect(() => {
    if (!clinicId) return undefined;
    const unsubscribe = subscribeDepartments(
      clinicId,
      (rows) => setDepartments(rows),
      (err) => console.error("[ClinicMessagingPage] departments subscription failed:", err),
    );
    return () => unsubscribe();
  }, [clinicId]);

  // Recipient preview: unique patients who checked in within the broadcast window,
  // optionally narrowed to the selected department's tagged check-ins.
  const recentPatientCount = useMemo(() => {
    const cutoff = Date.now() - VISIT_WINDOW_MS;
    const ids = new Set();
    visits.forEach((v) => {
      const d = toDate(v.checkedInAt);
      if (!v.patientId || !d || d.getTime() < cutoff) return;
      if (selectedDepartmentId && v.departmentId !== selectedDepartmentId) return;
      ids.add(v.patientId);
    });
    return ids.size;
  }, [visits, selectedDepartmentId]);

  const selectedDepartmentName = useMemo(() => {
    if (!selectedDepartmentId) return null;
    return departments.find((d) => d.id === selectedDepartmentId)?.name || null;
  }, [selectedDepartmentId, departments]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkInUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[ClinicMessagingPage] copy failed:", err);
    }
  };

  // Print just the QR + a "scan to check in" caption — a sheet for the waiting room.
  const handlePrint = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const win = window.open("", "_blank", "width=600,height=760");
    if (!win) return;
    win.document.write(`<!doctype html><html><head><title>Check-in QR</title>
      <style>
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; }
        .wrap { min-height: 100vh; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 24px; padding: 48px; text-align: center; }
        h1 { font-size: 32px; margin: 0; }
        p { font-size: 16px; color: #555; margin: 0; word-break: break-all; }
        .qr { padding: 24px; border: 1px solid #ddd; border-radius: 16px; }
      </style></head>
      <body><div class="wrap">
        <h1>Scan to check in</h1>
        <div class="qr">${svg.outerHTML}</div>
        <p>${checkInUrl}</p>
      </div></body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  const onSubmit = (values) => {
    setSendError(null);
    setSentSummary(null);
    setPendingValues(values);
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    if (!pendingValues) return;
    setSending(true);
    setSendError(null);
    try {
      const result = await sendClinicBroadcast({
        clinicId,
        body: pendingValues.body,
        departmentId: pendingValues.departmentId || undefined,
      });
      setSentSummary(result?.recipientCount ?? 0);
      reset({ body: "", departmentId: pendingValues.departmentId || "" });
      setConfirmOpen(false);
      setPendingValues(null);
    } catch (err) {
      setSendError(err.message);
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  if (!clinicId) {
    return (
      <div className="inner">
        <DashboardPageHeader
          title="Patient Messages"
          subtitle="Share a check-in code and message the patients who visit your clinic."
        />
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Patient Messages"
        subtitle="Share a check-in code and message the patients who visit your clinic."
      />

      {/* Section 1 — Check-in QR */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Check-in QR
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Display this in your waiting room. Patients scan it to check in, which
          lets you message them here.
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="center"
        >
          {/* QR card */}
          <Stack spacing={1} alignItems="center">
            <Box
              ref={qrRef}
              sx={{
                p: 2.5,
                bgcolor: "common.white",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <QRCodeSVG value={checkInUrl} size={196} level="H" />
            </Box>
            <Button
              size="small"
              color="inherit"
              startIcon={<PrintRounded fontSize="small" />}
              onClick={handlePrint}
              sx={{ color: "text.secondary" }}
            >
              Print
            </Button>
          </Stack>

          {/* Link + stats */}
          <Stack spacing={2.5} sx={{ flex: 1, width: "100%" }}>
            <TextField
              label="Check-in link"
              value={checkInUrl}
              size="small"
              fullWidth
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: "0.8rem" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied" : "Copy link"}>
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={handleCopy}
                        aria-label="Copy link"
                      >
                        {copied ? (
                          <CheckRounded fontSize="small" color="success" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <PeopleAltRounded color="primary" />
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {recentPatientCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  checked in over the last 30 days
                </Typography>
              </Box>
            </Stack>

            {visits.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Recent check-ins
                </Typography>
                <List dense disablePadding>
                  {visits.slice(0, 5).map((v) => {
                    const d = toDate(v.checkedInAt);
                    return (
                      <ListItem key={v.id} disableGutters>
                        <ListItemText
                          primary={d ? format(d, "MMM d, yyyy 'at' h:mm a") : "Just now"}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Section 2 — Send a message */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Send a message
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose an audience, then send. Your message appears in the recipients'
          Messages. Only patients who checked in over the last 30 days are included.
        </Typography>

        {sentSummary != null && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSentSummary(null)}
          >
            {sentSummary === 0
              ? "No recent check-ins to message yet. Your message was not sent."
              : `Message sent to ${sentSummary} patient${sentSummary === 1 ? "" : "s"}.`}
          </Alert>
        )}

        {sendError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSendError(null)}>
            {sendError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="departmentId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Audience"
                  fullWidth
                  helperText={
                    selectedDepartmentName
                      ? `Patients who checked in to ${selectedDepartmentName}.`
                      : "Everyone who checked in to your clinic."
                  }
                >
                  <MenuItem value="">All recent patients</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <PeopleAltRounded color="primary" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {recentPatientCount} recipient{recentPatientCount === 1 ? "" : "s"} in this audience
              </Typography>
            </Stack>
            <Controller
              name="body"
              control={control}
              rules={{ required: "Enter a message to send." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Message"
                  multiline
                  minRows={4}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Box>
              <LoadingButton type="submit" variant="contained" disabled={!isValid}>
                Send message
              </LoadingButton>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          if (!sending) {
            setConfirmOpen(false);
            setPendingValues(null);
          }
        }}
        title="Send this message?"
        message={
          recentPatientCount > 0
            ? `This will be sent to ${recentPatientCount} patient${
                recentPatientCount === 1 ? "" : "s"
              } who checked in over the last 30 days${
                selectedDepartmentName ? ` to ${selectedDepartmentName}` : ""
              }.`
            : selectedDepartmentName
            ? `No patients have checked in to ${selectedDepartmentName} over the last 30 days, so there is no one to send to yet.`
            : "No patients have checked in over the last 30 days, so there is no one to send to yet."
        }
        confirmLabel="Send"
        onConfirm={handleConfirmSend}
        loading={sending}
        confirmDisabled={recentPatientCount === 0}
      />
    </div>
  );
};

export default ClinicMessagingPage;
